import { toDate } from "@flaminem/utils";

import {
  CaseFragmentFragment,
  CaseAlertStatus as ApiCaseAlertStatus,
  BlacklistedIdentifierFragmentFragment,
  ShortCaseElementFragmentFragment,
  CaseSummaryFragmentFragment,
  BlacklistableIdentifierFragmentFragment,
  CaseType,
} from "src/graphql/anti-fraud/__generated__/graphql";
import { getPropertyValueByCode } from "src/services/property/getPropertyByCode";
import {
  BlacklistableCaseIdentifier,
  BlacklistedCaseIdentifier,
  Case,
  CaseAlertStatus,
  CaseSummary,
} from "src/types/case";
import { ShortElement } from "src/types/element";
import { hasType } from "src/types/typedValue";

import { mapToAdminStatus } from "./adminStatus.mappers";
import { mapToElement } from "./element.mappers";
import { mapIdentifier } from "./identifier.mapper";
import { mapToPropertyValue } from "./property.mappers";

export const mapToCase = (fragment: CaseFragmentFragment): Case => {
  const properties = fragment.properties.map(mapToPropertyValue);
  const requestProperty = getPropertyValueByCode(properties, "id");
  const request =
    requestProperty && hasType(requestProperty.value, "string")
      ? requestProperty.value.value
      : null;

  return {
    id: fragment.id,
    request,
    snapshotCreation: fragment.snapshotCreation ? toDate(fragment.snapshotCreation) : null,
    typeCode: fragment.typeCode,
    lastScore: fragment.lastScore,
    configurableType: {
      code: fragment.configurableType.code,
      name: fragment.configurableType.name,
    },
    alertStatus: mapToCaseAlertStatus(fragment.alertStatus),
    properties,
    elements: fragment.elements.map(mapToElement),
    blacklistedIdentifiers: fragment.blacklistedIdentifiers
      ? fragment.blacklistedIdentifiers.map(mapToBlacklistedCaseIdentifier)
      : [],
    adminStatus: mapToAdminStatus(fragment.adminStatus),
  };
};

export const mapToBlacklistableCaseIdentifier = (
  blacklistableCaseIdentifier: BlacklistableIdentifierFragmentFragment
): BlacklistableCaseIdentifier => {
  return {
    id: `${blacklistableCaseIdentifier.identifier.id}-${blacklistableCaseIdentifier.element.id}-${blacklistableCaseIdentifier.blacklist.code}`,
    element: {
      id: blacklistableCaseIdentifier.element.id,
      name: blacklistableCaseIdentifier.element.name,
      typeCode: blacklistableCaseIdentifier.element.typeCode,
    },
    identifier: {
      id: blacklistableCaseIdentifier.identifier.id,
      value: blacklistableCaseIdentifier.identifier.value,
      schemaIdentifier: {
        code: blacklistableCaseIdentifier.identifier.schemaIdentifier.code,
      },
      identifierType: {
        name: blacklistableCaseIdentifier.identifier.identifierType.name,
        code: blacklistableCaseIdentifier.identifier.identifierType.code,
      },
    },
    belongsToCurrentCase: blacklistableCaseIdentifier.belongsToCurrentCase,
    linkedCases: blacklistableCaseIdentifier.linkedCases.map((linkedCase) => ({
      id: linkedCase.id,
      properties: linkedCase.properties.map(mapToPropertyValue),
    })),
    blacklist: {
      name: blacklistableCaseIdentifier.blacklist.name,
      code: blacklistableCaseIdentifier.blacklist.code,
    },
  };
};

export const mapToShortCaseElement = (
  caseElement: ShortCaseElementFragmentFragment
): ShortElement => {
  return {
    id: caseElement.id,
    configurableType: {
      code: caseElement.configurableType.code,
      name: caseElement.configurableType.name,
    },
    properties: caseElement.properties.map(mapToPropertyValue),
  };
};

export const mapToCaseSummary = (fragment: CaseSummaryFragmentFragment): CaseSummary => {
  const properties = fragment.properties.map(mapToPropertyValue);

  const requestProperty = getPropertyValueByCode(properties, "id");
  const request =
    requestProperty && hasType(requestProperty.value, "string")
      ? requestProperty.value.value
      : null;

  return {
    id: fragment.id,
    lastScore: fragment.lastScore,
    request,
    properties,
    elements: fragment.elements.map(mapToElement),
    snapshotCreation: fragment.snapshotCreation ? toDate(fragment.snapshotCreation) : null,
    adminStatus: mapToAdminStatus(fragment.adminStatus),
    typeCode: fragment.typeCode,
  };
};

const CASE_STATUS_MAP: { [key in ApiCaseAlertStatus]: CaseAlertStatus } = {
  COMPLIANT: "compliant",
  FRAUD_PREVENTED: "fraudPrevented",
  INITIAL: "initial",
  PENDING_DECISION: "pendingDecision",
  PROVEN_FRAUD_WITH_FINANCIAL_LOSS: "provenFraudWithFinancialLoss",
  PROVEN_FRAUD_WITH_NO_FINANCIAL_LOSS: "provenFraudWithNoFinancialLoss",
  SCORED: "scored",
};

export const mapToCaseAlertStatus = (status: ApiCaseAlertStatus): CaseAlertStatus => {
  return CASE_STATUS_MAP[status];
};

export const mapToBlacklistedCaseIdentifier = (
  fragment: BlacklistedIdentifierFragmentFragment
): BlacklistedCaseIdentifier => ({
  element: {
    name: fragment.element.name,
    typeCode: fragment.element.typeCode,
  },
  identifier: mapIdentifier(fragment.identifier),
  blacklistElement: {
    blacklist: {
      name: fragment.blacklistElement.blacklist.name,
      code: fragment.blacklistElement.blacklist.code,
    },
  },
});

export const mapToCaseTabs = (
  caseType: Pick<CaseType, "code" | "name">
): { code: string; label: string } => ({
  code: caseType.code,
  label: caseType.name,
});
